// PR-MAY16-INVESTOR-RATES-V2 item 4 — bulk apply + copy from template.
//
// Two inline dialogs mounted from InvestorRateSheet's toolbar:
//   * BulkApplyDialog  — pick contracts + one SAR/trip → bulk insert.
//   * CopyFromDialog   — pick a source investor → copy entire rate
//                        sheet across (optional overwrite).
//
// Both dialogs invalidate ['investor-site-rates', investorId] on success
// so the table refreshes without a page reload.

import { useState, useMemo, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useQuery } from '@tanstack/react-query';
import { apiClient, extractErrorMessage } from '@/lib/api';
import {
  useBulkApplyInvestorSiteRate,
  useCopyRatesFromInvestor,
} from '@/hooks/useInvestorSiteRates';

interface SiteLite { id: string; name_en: string; }
interface InvestorLite { id: string; code: string; fullNameEn: string; }

// PR-MAY16-RATE-DROPDOWN-FIX — bulk picks SITES, not contracts.
export function BulkApplyDialog({
  investorId, onClose,
}: {
  investorId: string;
  onClose: (result?: { created: number; skipped: number }) => void;
}) {
  const { t } = useTranslation();
  const mut = useBulkApplyInvestorSiteRate(investorId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sitesQ = useQuery({
    queryKey: ['sites-list'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items?: Array<{ id: string; nameEn: string }> } | Array<{ id: string; nameEn: string }>>(
        `/api/v1/sites?pageSize=200`,
      );
      const items = Array.isArray(data) ? data : (data.items ?? []);
      return items.map((s) => ({ id: s.id, name_en: s.nameEn }));
    },
  });
  const sites: SiteLite[] = sitesQ.data ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (selected.size === 0) { setError(t('investors.bulkApply.errors.noSites')); return; }
    if (!rate) { setError(t('investors.bulkApply.errors.noRate')); return; }
    mut.mutate(
      {
        investorId,
        siteIds: Array.from(selected),
        investorRateAtTarget: Number(rate),
      },
      {
        onSuccess: (r) => onClose({ created: r.created, skipped: r.skipped }),
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-bulk-apply-dialog>
      <form onSubmit={submit} className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-display text-foreground">{t('investors.bulkApply.title')}</h3>
          <p className="text-xs text-muted-foreground mt-1">{t('investors.bulkApply.subtitle')}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <Input
            type="number" step="0.01" min="0" inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder={t('investors.bulkApply.field.rate') as string}
            className="text-end tabular-nums"
            data-bulk-apply-rate
            required
          />
          <div className="space-y-1 max-h-[40vh] overflow-y-auto border border-border rounded p-2">
            {sitesQ.isLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
            ) : sites.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('investors.bulkApply.noSites')}</p>
            ) : (
              sites.map((s) => (
                <label key={s.id} className="flex items-center gap-2 py-1 px-1 cursor-pointer hover:bg-muted/40 rounded">
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                    data-bulk-apply-site={s.id}
                  />
                  <span className="text-sm">{s.name_en}</span>
                </label>
              ))
            )}
          </div>
          {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2 bg-muted/30">
          <Button type="button" variant="outline" size="sm" onClick={() => onClose()} disabled={mut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="gold" size="sm" disabled={mut.isPending || selected.size === 0}>
            {mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('investors.bulkApply.apply', { count: selected.size })}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function CopyFromDialog({
  investorId, onClose,
}: {
  investorId: string;
  onClose: (result?: { created: number; updated: number; skipped: number }) => void;
}) {
  const { t } = useTranslation();
  const mut = useCopyRatesFromInvestor(investorId);
  const [sourceInvestorId, setSourceInvestorId] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const investorsQ = useQuery({
    queryKey: ['active-investors-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: InvestorLite[] }>(
        `/api/v1/investors?status=active&pageSize=200`,
      );
      // Exclude self — can't copy from yourself.
      return (data.items ?? []).filter((i) => i.id !== investorId);
    },
  });
  const investors = useMemo(() => investorsQ.data ?? [], [investorsQ.data]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!sourceInvestorId) { setError(t('investors.copyFrom.errors.noSource')); return; }
    mut.mutate(
      { targetInvestorId: investorId, sourceInvestorId, overwrite },
      {
        onSuccess: (r) => onClose({ created: r.created, updated: r.updated, skipped: r.skipped }),
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-copy-from-dialog>
      <form onSubmit={submit} className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-display text-foreground">{t('investors.copyFrom.title')}</h3>
          <p className="text-xs text-muted-foreground mt-1">{t('investors.copyFrom.subtitle')}</p>
        </div>
        <div className="p-4 space-y-3">
          <select
            value={sourceInvestorId}
            onChange={(e) => setSourceInvestorId(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
            data-copy-from-source
            required
          >
            <option value="">{t('investors.copyFrom.field.source')}</option>
            {investors.map((i) => (
              <option key={i.id} value={i.id}>{i.fullNameEn} ({i.code})</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              data-copy-from-overwrite
            />
            {t('investors.copyFrom.field.overwrite')}
          </label>
          {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2 bg-muted/30">
          <Button type="button" variant="outline" size="sm" onClick={() => onClose()} disabled={mut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="gold" size="sm" disabled={mut.isPending}>
            {mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('investors.copyFrom.copy')}
          </Button>
        </div>
      </form>
    </div>
  );
}
